import { useEffect, useMemo, useState } from 'react';
import {
  Alert as MuiAlert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, ContentCopy, Key, Autorenew, DeleteOutline } from '@mui/icons-material';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listCuitProfiles } from '@/api/cuitProfiles';
import { listDeveloperApplications, type DeveloperApplication } from '@/api/applications';
import {
  issueApiCredential,
  listApiCredentials,
  revokeApiCredential,
  rotateApiCredential,
} from '@/api/apiCredentials';
import type {
  ApiCredentialResponse,
  ApiEnvironment,
  CuitProfileResponse,
  IssueApiCredentialResponse,
} from '@/api/types';
import { toApiError } from '@/api/client';
import { useCurrentCuit } from '@/auth/CuitContext';
import { EmptyState } from '@/components/EmptyState';
import { ApiProductRouteGuard } from '@/components/ApiProductRouteGuard';
import { LoadingScreen } from '@/components/LoadingScreen';
import { EnvironmentBadge } from '@/components/primitives';
import { formatCuit, formatDateTime } from '@/lib/formatters';
import { API_CREDENTIAL_STATUS_COLOR, API_CREDENTIAL_STATUS_LABEL } from '@/lib/labels';

export const Route = createFileRoute('/_authenticated/credenciales/')({
  component: CredencialesPage,
});

const SANDBOX_SCOPES = [
  'invoicing:read',
  'invoicing:write',
  'webhooks:read',
  'webhooks:write',
  'webhooks:operations',
] as const;

const PRODUCTION_SCOPES = [
  ...SANDBOX_SCOPES,
  'customers:read',
  'customers:write',
  'cuit-profiles:read',
  'taxpayers:read',
  'documents:read',
] as const;

export function credentialScopesForEnvironment(environment: ApiEnvironment): readonly string[] {
  return environment === 'Sandbox' ? SANDBOX_SCOPES : PRODUCTION_SCOPES;
}

interface Revealed {
  clientId: string;
  clientSecret: string;
  /** Texto contextual: ventana de gracia (rotación) o null (alta). */
  note: string | null;
}

function CredencialesPage(): JSX.Element {
  return (
    <ApiProductRouteGuard>
      <CredencialesContent />
    </ApiProductRouteGuard>
  );
}

function CredencialesContent(): JSX.Element {
  const queryClient = useQueryClient();
  const { currentCuitProfileId } = useCurrentCuit();
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [revealed, setRevealed] = useState<Revealed | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [confirm, setConfirm] = useState<{ kind: 'rotate' | 'revoke'; cred: ApiCredentialResponse } | null>(null);

  const applicationsQ = useQuery({
    queryKey: ['developer-applications'],
    queryFn: () => listDeveloperApplications(0, 100),
  });
  const profilesQ = useQuery({
    queryKey: ['cuit-profiles', 'credenciales'],
    queryFn: () => listCuitProfiles(0, 200),
  });
  const applications = useMemo(() => applicationsQ.data?.items ?? [], [applicationsQ.data]);
  const profiles = useMemo(() => profilesQ.data?.items ?? [], [profilesQ.data]);
  const productionProfiles = useMemo(
    () => profiles.filter((profile) => profile.environment === 'Production'),
    [profiles],
  );

  // Conserva la selección mientras exista; si no, prioriza una aplicación activa.
  useEffect(() => {
    if (applications.length === 0) {
      if (applicationId !== null) setApplicationId(null);
      return;
    }
    if (applications.some((application) => application.id === applicationId)) return;
    setApplicationId(
      applications.find((application) => application.status === 'Active')?.id ?? applications[0]!.id,
    );
  }, [applicationId, applications]);

  const selectedApplication = applications.find((application) => application.id === applicationId) ?? null;
  const activeApplications = applications.filter((application) => application.status === 'Active');

  const credsQ = useQuery({
    queryKey: ['api-credentials', applicationId],
    queryFn: () => listApiCredentials(applicationId!, 0, 100),
    enabled: Boolean(applicationId),
  });

  function invalidate(targetApplicationId = applicationId): void {
    void queryClient.invalidateQueries({ queryKey: ['api-credentials', targetApplicationId] });
  }
  function onError(err: unknown): void {
    setToast({ message: toApiError(err).message, severity: 'error' });
  }

  const rotateMut = useMutation({
    mutationFn: (id: string) => rotateApiCredential(id),
    onSuccess: (res) => {
      setConfirm(null);
      invalidate();
      setRevealed({
        clientId: res.clientId,
        clientSecret: res.clientSecret,
        note: res.previousSecretRetiresAt
          ? `El secret anterior sigue válido hasta ${formatDateTime(res.previousSecretRetiresAt)} (ventana de gracia).`
          : null,
      });
    },
    onError,
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) => revokeApiCredential(id),
    onSuccess: () => {
      setConfirm(null);
      invalidate();
      setToast({ message: 'Credencial revocada.', severity: 'success' });
    },
    onError,
  });

  const creds = credsQ.data?.items ?? [];
  const profilesById = new Map(profiles.map((profile) => [profile.id, profile]));
  const canIssue = selectedApplication?.status === 'Active'
    && (selectedApplication.environment === 'Sandbox' || productionProfiles.length > 0);

  if (applicationsQ.isLoading) return <LoadingScreen message="Cargando credenciales…" />;

  if (applicationsQ.error) {
    return <MuiAlert severity="error">{toApiError(applicationsQ.error).message}</MuiAlert>;
  }

  if (applications.length === 0) {
    return (
      <Box>
        <Header
          onNew={() => undefined}
          canIssue={false}
          applicationId={null}
          applications={[]}
          onApplication={() => undefined}
        />
        <EmptyState
          icon={<Key fontSize="inherit" />}
          title="No hay aplicaciones"
          description="Creá una aplicación Sandbox o Production antes de emitir credenciales."
        />
      </Box>
    );
  }

  return (
    <Box>
      <Header
        onNew={() => setIssuing(true)}
        canIssue={canIssue}
        applicationId={applicationId}
        applications={applications}
        onApplication={setApplicationId}
      />

      {selectedApplication?.status === 'Disabled' && (
        <MuiAlert severity="warning" sx={{ mb: 2 }}>
          La aplicación seleccionada está deshabilitada. Podés consultar sus credenciales, pero no emitir nuevas.
        </MuiAlert>
      )}
      {selectedApplication?.environment === 'Production' && profilesQ.isLoading && (
        <MuiAlert severity="info" sx={{ mb: 2 }}>Cargando CUITs productivos…</MuiAlert>
      )}
      {selectedApplication?.environment === 'Production' && profilesQ.error && (
        <MuiAlert severity="error" sx={{ mb: 2 }}>{toApiError(profilesQ.error).message}</MuiAlert>
      )}
      {selectedApplication?.environment === 'Production'
        && !profilesQ.isLoading
        && !profilesQ.error
        && productionProfiles.length === 0 && (
          <MuiAlert severity="warning" sx={{ mb: 2 }}>
            Production exige un CUIT configurado en AFIP Producción antes de emitir la credencial.
          </MuiAlert>
        )}

      {credsQ.isLoading ? (
        <LoadingScreen message="Cargando credenciales…" />
      ) : credsQ.error ? (
        <MuiAlert severity="error">{toApiError(credsQ.error).message}</MuiAlert>
      ) : creds.length === 0 ? (
        <EmptyState
          icon={<Key fontSize="inherit" />}
          title="Sin credenciales"
          description="Emití una credencial OAuth 2.0 para esta aplicación y su ambiente."
          actionLabel={canIssue ? 'Nueva credencial' : undefined}
          onAction={canIssue ? () => setIssuing(true) : undefined}
        />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre / Client ID</TableCell>
                <TableCell>Ambiente / CUIT</TableCell>
                <TableCell>Scopes</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Último uso</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {creds.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'var(--font-mono)' }}>
                      {c.clientId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5} alignItems="flex-start">
                      <EnvironmentBadge environment={c.environment} />
                      <Typography variant="caption" color="text.secondary">
                        {c.cuitProfileId
                          ? formatCuit(profilesById.get(c.cuitProfileId)?.cuit ?? c.cuitProfileId)
                          : 'Sin identidad fiscal'}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                      {c.scopes.map((s) => <Chip key={s} size="small" variant="outlined" label={s} />)}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" color={API_CREDENTIAL_STATUS_COLOR[c.status]} label={API_CREDENTIAL_STATUS_LABEL[c.status]} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {c.lastUsedAt ? formatDateTime(c.lastUsedAt) : 'Nunca'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {c.status === 'Active' ? (
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Rotar secret (dual-secret, ventana de gracia 24h)">
                          <span>
                            <IconButton size="small" disabled={rotateMut.isPending} onClick={() => setConfirm({ kind: 'rotate', cred: c })}>
                              <Autorenew fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Revocar">
                          <span>
                            <IconButton size="small" color="error" disabled={revokeMut.isPending} onClick={() => setConfirm({ kind: 'revoke', cred: c })}>
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {issuing && activeApplications.length > 0 && (
        <IssueDialog
          applications={activeApplications}
          initialApplicationId={selectedApplication?.status === 'Active' ? selectedApplication.id : activeApplications[0]!.id}
          productionProfiles={productionProfiles}
          preferredCuitProfileId={currentCuitProfileId}
          onClose={() => setIssuing(false)}
          onIssued={(res) => {
            setIssuing(false);
            setApplicationId(res.developerApplicationId);
            invalidate(res.developerApplicationId);
            setRevealed({ clientId: res.clientId, clientSecret: res.clientSecret, note: null });
          }}
          onError={onError}
        />
      )}

      {confirm && (
        <Dialog open onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
          <DialogTitle>{confirm.kind === 'rotate' ? 'Rotar secret' : 'Revocar credencial'}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {confirm.kind === 'rotate'
                ? `Se generará un secret nuevo para "${confirm.cred.name}". El anterior seguirá válido durante una ventana de gracia para que migres sin downtime.`
                : `Vas a revocar "${confirm.cred.name}". Las integraciones que la usen dejarán de funcionar de inmediato. Esta acción no se puede deshacer.`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirm(null)}>Cancelar</Button>
            <Button
              variant="contained"
              color={confirm.kind === 'revoke' ? 'error' : 'primary'}
              disabled={rotateMut.isPending || revokeMut.isPending}
              onClick={() => (confirm.kind === 'rotate' ? rotateMut.mutate(confirm.cred.id) : revokeMut.mutate(confirm.cred.id))}
            >
              {confirm.kind === 'rotate' ? 'Rotar' : 'Revocar'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {revealed && <SecretDialog revealed={revealed} onClose={() => setRevealed(null)} onCopied={() => setToast({ message: 'Copiado al portapapeles.', severity: 'success' })} />}

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <MuiAlert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)} variant="filled">
          {toast?.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}

function Header({
  onNew,
  canIssue,
  applicationId,
  applications,
  onApplication,
}: {
  onNew: () => void;
  canIssue: boolean;
  applicationId: string | null;
  applications: DeveloperApplication[];
  onApplication: (id: string) => void;
}): JSX.Element {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }} flexWrap="wrap" gap={2}>
      <Box>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Key color="primary" /> Credenciales
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Credenciales OAuth 2.0 por aplicación y ambiente, con rotación dual-secret.
        </Typography>
      </Box>
      <Stack direction="row" spacing={1.5} alignItems="center">
        {applications.length > 0 && (
          <TextField
            select
            size="small"
            label="Aplicación"
            value={applicationId ?? ''}
            onChange={(e) => onApplication(e.target.value)}
            sx={{ minWidth: 280 }}
          >
            {applications.map((application) => (
              <MenuItem key={application.id} value={application.id}>
                {application.name} · {application.environment}
                {application.status === 'Disabled' ? ' · Deshabilitada' : ''}
              </MenuItem>
            ))}
          </TextField>
        )}
        <Button variant="contained" startIcon={<Add />} disabled={!canIssue} onClick={onNew}>
          Nueva credencial
        </Button>
      </Stack>
    </Stack>
  );
}

export function IssueDialog({
  applications,
  initialApplicationId,
  productionProfiles,
  preferredCuitProfileId,
  onClose,
  onIssued,
  onError,
}: {
  applications: DeveloperApplication[];
  initialApplicationId: string;
  productionProfiles: CuitProfileResponse[];
  preferredCuitProfileId: string | null;
  onClose: () => void;
  onIssued: (res: IssueApiCredentialResponse) => void;
  onError: (err: unknown) => void;
}): JSX.Element {
  const initialApplication = applications.find((application) => application.id === initialApplicationId)
    ?? applications[0]!;
  const initialProfileId = initialApplication.environment === 'Production'
    ? productionProfiles.find((profile) => profile.id === preferredCuitProfileId)?.id
      ?? productionProfiles[0]?.id
      ?? null
    : null;
  const [applicationId, setApplicationId] = useState(initialApplication.id);
  const [profileId, setProfileId] = useState<string | null>(initialProfileId);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['invoicing:read', 'invoicing:write']);
  const [expiresAt, setExpiresAt] = useState('');
  const selectedApplication = applications.find((application) => application.id === applicationId)
    ?? applications[0]!;
  const availableScopes = credentialScopesForEnvironment(selectedApplication.environment);

  function selectApplication(nextApplicationId: string): void {
    const next = applications.find((application) => application.id === nextApplicationId)
      ?? applications[0]!;
    setApplicationId(next.id);
    setScopes(['invoicing:read', 'invoicing:write']);
    setProfileId(next.environment === 'Production'
      ? productionProfiles.find((profile) => profile.id === preferredCuitProfileId)?.id
        ?? productionProfiles[0]?.id
        ?? null
      : null);
  }

  const mut = useMutation({
    mutationFn: () =>
      issueApiCredential(selectedApplication.id, {
        name: name.trim(),
        scopes,
        expiresAt: expiresAt ? new Date(`${expiresAt}T00:00:00`).toISOString() : null,
        cuitProfileId: selectedApplication.environment === 'Production' ? profileId : null,
      }),
    onSuccess: onIssued,
    onError,
  });

  const toggle = (scope: string): void =>
    setScopes((cur) => (cur.includes(scope) ? cur.filter((s) => s !== scope) : [...cur, scope]));

  const valid = name.trim().length > 0
    && scopes.length > 0
    && (selectedApplication.environment === 'Sandbox' || Boolean(profileId));

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Nueva credencial</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <TextField
            select
            label="Aplicación"
            fullWidth
            value={selectedApplication.id}
            onChange={(event) => selectApplication(event.target.value)}
          >
            {applications.map((application) => (
              <MenuItem key={application.id} value={application.id}>
                {application.name} · {application.environment}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2">Ambiente fijado:</Typography>
            <EnvironmentBadge environment={selectedApplication.environment} />
          </Stack>
          {selectedApplication.environment === 'Sandbox' ? (
            <MuiAlert severity="info">
              Esta credencial usa <code>/sandbox/invoices</code>, no requiere CUIT y no produce efectos fiscales.
            </MuiAlert>
          ) : productionProfiles.length === 0 ? (
            <MuiAlert severity="warning">
              No hay un CUIT configurado en AFIP Producción. Debés crear uno antes de emitir esta credencial.
            </MuiAlert>
          ) : (
            <TextField
              select
              required
              label="CUIT productivo"
              fullWidth
              value={profileId ?? ''}
              onChange={(event) => setProfileId(event.target.value)}
              helperText="La credencial sólo podrá operar con este perfil fiscal."
            >
              {productionProfiles.map((profile) => (
                <MenuItem key={profile.id} value={profile.id}>
                  {profile.razonSocial} · {formatCuit(profile.cuit)}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField label="Nombre" fullWidth value={name} onChange={(e) => setName(e.target.value)} placeholder="Integración facturación" autoFocus />
          <Box>
            <Typography variant="subtitle2" gutterBottom>Scopes</Typography>
            <Stack>
              {availableScopes.map((s) => (
                <FormControlLabel key={s} control={<Checkbox checked={scopes.includes(s)} onChange={() => toggle(s)} />} label={s} />
              ))}
            </Stack>
          </Box>
          <TextField
            label="Vencimiento (opcional)"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            helperText="Sin fecha = no expira."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" disabled={!valid || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? <CircularProgress size={18} /> : 'Emitir'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function SecretDialog({ revealed, onClose, onCopied }: { revealed: Revealed; onClose: () => void; onCopied: () => void }): JSX.Element {
  function copy(text: string): void {
    void navigator.clipboard?.writeText(text).then(onCopied);
  }
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="secret-dialog-title">
      <DialogTitle id="secret-dialog-title">Credencial generada</DialogTitle>
      <DialogContent dividers>
        <MuiAlert severity="warning" sx={{ mb: 2 }}>
          Guardá el <strong>client secret</strong> ahora: se muestra una sola vez y no se puede recuperar.
        </MuiAlert>
        <SecretField label="Client ID" value={revealed.clientId} onCopy={() => copy(revealed.clientId)} />
        <SecretField label="Client Secret" value={revealed.clientSecret} onCopy={() => copy(revealed.clientSecret)} mono />
        {revealed.note && (
          <MuiAlert severity="info" sx={{ mt: 2 }}>{revealed.note}</MuiAlert>
        )}
      </DialogContent>
      <DialogActions>
        {/* autoFocus: el foco aterriza en la acción segura de cierre al abrir el diálogo. */}
        <Button variant="contained" onClick={onClose} autoFocus>Listo, lo guardé</Button>
      </DialogActions>
    </Dialog>
  );
}

function SecretField({ label, value, onCopy, mono }: { label: string; value: string; onCopy: () => void; mono?: boolean }): JSX.Element {
  const labelId = `secret-${label.toLowerCase().replace(/\s+/g, '-')}-label`;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography id={labelId} variant="caption" color="text.secondary" component="div">{label}</Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          role="textbox"
          aria-readonly="true"
          aria-labelledby={labelId}
          tabIndex={0}
          sx={{
            flex: 1,
            p: 1,
            borderRadius: 1,
            bgcolor: 'action.hover',
            fontFamily: mono ? 'var(--font-mono)' : undefined,
            fontSize: 13,
            wordBreak: 'break-all',
          }}
        >
          {value}
        </Box>
        <Tooltip title="Copiar">
          <IconButton size="small" onClick={onCopy} aria-label={`Copiar ${label}`}><ContentCopy fontSize="small" /></IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}
